import type { PosDatabase } from '@core/db/db'
import { META_ACTIVE_SHIFT_ID, META_DEVICE_ID, META_DEVICE_TOKEN, META_STORE_ID, META_ZONE_ID } from '@core/db/schema'
import type { PosShift } from '@core/db/models'
import { syncService, type ShiftZReport } from '@core/services/sync/SyncService'
import type { NativeKktDocument } from '@core/native/kkt'
import { reconcileKktDocuments } from './kktReconcile'
import { autoCloseStaleShift } from './shiftFreshness'

export interface OpenShiftParams {
  cashierId: string
  cashierName: string
  initialCash?: number
  fiscalizationEnabled?: boolean
}

export interface CloseShiftOptions {
  /** При подключённой ККТ требуем успешную сверку Z, а не только отсутствие external. */
  requireKktZ?: boolean;
  /**
   * Касса умеет читать документы смены ККТ (native). В веб/агент-режиме
   * сверка технически недоступна: закрытие не блокируем по «неизвестно»,
   * но блокируем по явной неудаче сверки (`kktZComplete === false`).
   */
  kktDocumentsReadable?: boolean;
  /** Закрытие смены на ККТ. Вызывается после локальных preflight-проверок. */
  kktClose?: () => Promise<{ success: boolean; error?: string }>;
}

export interface ShiftSummary {
  shift: PosShift
  salesCount: number
  totalRevenue: number
  /** Сумма возвратов за смену (положительная) — ARCH-TASK-65. */
  returnsTotal: number
  returnsCount: number
  cashTotal: number
  cardTotal: number
  sbpTotal: number
  /** Локальный вклад ЭТОЙ кассы (фиксируется всегда — нужен офлайну). */
  partial?: {
    salesCount: number
    totalSales: number
    cashTotal: number
    cardTotal: number
    sbpTotal: number
    returnsTotal?: number
    returnsCount?: number
  }
  /** Полная агрегация Z по ЗОНЕ из облака (есть при онлайн-закрытии). */
  full?: ShiftZReport
  /** true — Z неполный (касса была офлайн): итог по зоне появится в облаке. */
  incomplete?: boolean
}

/**
 * 65.14: смена не закрывается, пока есть hold-чеки (`marking_review`).
 * Вынесено отдельной функцией, чтобы вызывать из UI/тестов без сети.
 */
export async function assertNoHeldReceipts(db: PosDatabase): Promise<void> {
  const held = await db.receipts.where('status').equals('marking_review').count()
  if (held > 0) {
    throw new Error(
      `Есть ${held} чек(ов) с маркировкой, ожидающих решения. ` +
        'Одобрите/отмените их или дождитесь автопробития.'
    )
  }
}

/**
 * 9d/P1: чек уже пробит/отменён, но смена не получила hold-счётчики.
 * Это возможно, если `releaseHold/cancelHold` вернул false (например, смена
 * успела измениться). Закрывать Z в таком состоянии нельзя: деньги и чек
 * разъедутся.
 */
export async function assertNoUnaccountedHolds(db: PosDatabase): Promise<void> {
  const rows = await db.receipts.where('status').anyOf('fiscalized', 'cancelled').toArray()
  const pending = rows.filter((receipt) => receipt.markingAppliedAt && !receipt.holdShiftAppliedAt)
  if (pending.length > 0) {
    throw new Error(
      `Есть ${pending.length} чек(ов), применённых по маркировке, но не учтённых в смене. ` +
        'Перезапустите кассу для recovery или обратитесь к администратору.'
    )
  }
}

/**
 * B3/65.14: смена не закрывается, пока есть незавершённые замены
 * non_fiscal → fiscal. Иначе фискальный чек окажется в следующей смене,
 * а Z предыдущей уже уйдёт без него.
 */
export async function assertNoPendingReplacements(db: PosDatabase): Promise<void> {
  const rows = await db.receipts.where('status').equals('non_fiscal').toArray()
  const pending = rows.filter(
    (r) =>
      !r.trash &&
      (r.replacementState === 'awaiting_verification' ||
        r.replacementState === 'awaiting_print' ||
        r.replacementState === 'processing' ||
        r.replacementState === 'failed')
  )
  if (pending.length > 0) {
    throw new Error(
      `Есть ${pending.length} чек(ов), ожидающих замены на фискальный. ` +
        'Напечатайте фискальный чек или отмените замену с причиной.'
    )
  }
}

/**
 * Служба управления кассовыми сменами.
 *
 * Смена принадлежит ЗОНЕ (рабочему месту/отделу). Первое устройство зоны
 * ОТКРЫВАЕТ смену, остальные устройства зоны ПРИСОЕДИНЯЮТСЯ к ней (свою не
 * заводят). Нумерация — по зоне. На кассе смену закрывает только
 * устройство-открыватель (openedByDevice) либо директор из админки. Открытая
 * смена старше 24 ч авто-закрывается (таймаут).
 *
 * Жизненный цикл: openShift → recordSale(...) → closeShift (Z-отчёт).
 * Активная смена дублируется в meta (activeShiftId) для быстрого доступа.
 */
export class ShiftService {
  constructor(private readonly db: PosDatabase) {}

  /** Прочитать JWT кассы из meta (или null, если касса не подключена). */
  private async authToken(): Promise<string | null> {
    const token = await this.db.meta.get(META_DEVICE_TOKEN)
    return typeof token?.value === 'string' && token.value ? token.value : null
  }

  /** zone/device из локальной БД, без обращения к глобальному syncService.db. */
  private async localMetaString(key: string): Promise<string | null> {
    const entry = await this.db.meta.get(key)
    return typeof entry?.value === 'string' && entry.value ? entry.value : null
  }

  private async localZoneId(): Promise<string> {
    const zone = await this.localMetaString(META_ZONE_ID)
    if (zone) return zone
    // Совместимость со старыми/тестовыми БД: зона могла быть только в смене.
    const openShift = await this.db.shifts.where('status').equals('open').first()
    if (openShift?.zoneId) return openShift.zoneId
    const device = await this.localDeviceId()
    await this.db.meta.put({ key: META_ZONE_ID, value: device })
    return device
  }

  private async localDeviceId(): Promise<string> {
    const device = await this.localMetaString(META_DEVICE_ID)
    if (device) return device
    // Fallback только для самой первой инициализации: syncService создаст
    // канонический device id (phone-.../pos-terminal-1).
    const created = await syncService.ensureDeviceId()
    await this.db.meta.put({ key: META_DEVICE_ID, value: created })
    return created
  }

  /**
   * Фоновая отправка смен в облако (fire-and-forget). Не блокирует кассу и
   * молча пропускается без сети/токена — надёжная доставка обеспечивается
   * периодическим циклом syncAll и идемпотентным upsert на сервере.
   */
  private pushShiftsInBackground(): void {
    void (async () => {
      try {
        if (!navigator.onLine) return
        const token = await this.db.meta.get(META_DEVICE_TOKEN)
        const tokenStr = typeof token?.value === 'string' ? token.value : null
        if (!tokenStr) return
        await syncService.pushShifts(tokenStr)
      } catch (err) {
        console.warn('[ShiftService] background shift sync failed:', err)
      }
    })()
  }

  /**
   * Дослать накопленные чеки в облако перед закрытием смены (forward-look):
   * Z-отчёт по зоне должен учитывать все продажи, в т.ч. с других устройств.
   * Best-effort: офлайн/ошибка не блокируют закрытие смены.
   */
  private async flushReceipts(): Promise<void> {
    try {
      if (!navigator.onLine) return
      const token = await this.authToken()
      if (!token) return
      await syncService.pushReceipts(token)
    } catch (err) {
      console.warn('[ShiftService] forward-look receipts failed:', err)
    }
  }

  /**
   * Проверить свежесть активной смены: если она открыта дольше 24 ч —
   * авто-закрыть (reason='timeout') и снять activeShiftId.
   * @returns номер авто-закрытой смены или null.
   */
  async ensureShiftFreshness(): Promise<number | null> {
    const closedNumber = await autoCloseStaleShift(this.db)
    if (closedNumber != null) {
      // Сообщаем облаку о закрытии (мониторинг директора и реконсиляция зоны).
      this.pushShiftsInBackground()
    }
    return closedNumber
  }

  /**
   * Получить текущую открытую смену МОЕЙ ЗОНЫ или null.
   * Смена без zoneId создана до введения зон — считаем её своей.
   */
  async getActiveShift(): Promise<PosShift | null> {
    const shift = await this.db.shifts.where('status').equals('open').first()
    if (!shift) return null
    const zoneId = await this.localZoneId()
    if (shift.zoneId && shift.zoneId !== zoneId) return null
    return shift
  }

  /**
   * Открыть смену зоны или ПРИСОЕДИНИТЬСЯ к уже открытой.
   *
   * Если в зоне уже есть открытая смена (локально или принятая из облака через
   * pullShifts) — возвращаем её и НЕ создаём новую. Иначе создаём с номером
   * max(номер смен зоны) + 1 и фиксируем зону и устройство-открывателя.
   */
  async openShift(params: OpenShiftParams): Promise<PosShift> {
    const active = await this.getActiveShift()
    if (active) return active
    const online = typeof navigator === 'undefined' || navigator.onLine
    if (online === false) {
      throw new Error('Нет активной смены. Новую смену зоны нельзя открыть офлайн — подключите сеть.')
    }
    const zoneId = await this.localZoneId()
    const deviceId = await this.localDeviceId()
    const storeId = await this.localMetaString(META_STORE_ID)
    const server = await syncService.openShiftOnServer(zoneId, deviceId, params.cashierName, params.fiscalizationEnabled !== false)
    if (server == null || server.id == null || server.shift_number == null) {
      throw new Error('Сервер не вернул открытую смену')
    }
    const shift: PosShift = {
      shiftId: String(server.shift_id ?? server.id),
      shiftNumber: Number(server.shift_number),
      cashierId: params.cashierId,
      cashierName: params.cashierName,
      openedAt: Date.parse(server.opened_at) || Date.now(),
      status: 'open',
      fiscalizationEnabled: params.fiscalizationEnabled !== false,
      initialCash: params.initialCash ?? 0,
      totalSales: 0, totalCash: 0, totalCard: 0, totalSbp: 0, salesCount: 0,
      zoneId,
      storeId: storeId ?? undefined,
      openedByDevice: server.opened_by_device ?? deviceId
    }
    const id = (await this.db.shifts.add(shift)) as number
    await this.db.meta.put({ key: META_ACTIVE_SHIFT_ID, value: id })
    // Сообщаем облаку об открытии смены (мониторинг касс директором).
    this.pushShiftsInBackground()
    return { ...shift, id }
  }

  /**
   * Обновить локальную смену по read-only документам ККТ.
   *
   * Документы открытия/закрытия смены не считаются чеками. Всё, чего нет в
   * локальных фискальных чеках POSPoint, считается внешним вкладом другого
   * ПО. Такой Z нельзя считать полным.
   */
  async syncKktDocuments(
    shiftNumber: number,
    documents: NativeKktDocument[],
    options: { incremental?: boolean; complete?: boolean } = {}
  ): Promise<PosShift | null> {
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null || shift.shiftNumber !== shiftNumber) return null

    const localReceipts = (
      await this.db.receipts.where('shiftNumber').equals(shiftNumber).toArray()
    ).filter(
      (receipt) => !shift.zoneId || !receipt.zoneId || receipt.zoneId === shift.zoneId
    )
    const localFiscalNumbers = localReceipts
      .map((receipt) => receipt.fiscalData?.fiscalDocumentNumber)
      .filter((value): value is number => typeof value === 'number')

    const reconciled = reconcileKktDocuments({
      shiftNumber,
      documents,
      localFiscalNumbers,
      incremental: options.incremental === true,
      complete: options.complete !== false,
      previous: {
        documentCount: shift.kktDocumentCount,
        externalReceipts: shift.kktExternalReceipts,
        externalDocumentNumbers: shift.kktExternalDocumentNumbers,
        lastDocNumber: shift.kktLastDocNumber,
        unknownDocuments: shift.kktUnknownDocuments
      }
    })
    const syncedAt = Date.now()
    const patch: Partial<PosShift> = {
      kktLastDocNumber: reconciled.lastDocNumber,
      kktDocumentCount: reconciled.documentCount,
      kktExternalReceipts: reconciled.externalReceipts,
      kktExternalDocumentNumbers: reconciled.externalDocumentNumbers,
      kktUnknownDocuments: reconciled.unknownDocuments,
      kktSyncedAt: syncedAt,
      kktZComplete: reconciled.zComplete,
      kktSyncError: reconciled.syncError
    }
    await this.db.shifts.update(shift.id, patch)
    return { ...shift, ...patch }
  }

  /**
   * Явно пометить сверку смены как неуспешную. Нужно при сетевой/драйверной
   * ошибке чтения: старый `kktZComplete=true` нельзя оставлять.
   */
  async markKktSyncFailed(shiftNumber: number, error: string): Promise<PosShift | null> {
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null || shift.shiftNumber !== shiftNumber) return null
    const message = error.trim() || 'kkt_documents_read_failed'
    await this.db.shifts.update(shift.id, {
      kktZComplete: false,
      kktSyncError: message,
      kktSyncedAt: Date.now()
    })
    return { ...shift, kktZComplete: false, kktSyncError: message, kktSyncedAt: Date.now() }
  }

  /** Учесть продажу в текущей открытой смене. */
  async recordSale(amount: number, method = 'cash'): Promise<void> {
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) return

    await this.db.shifts.update(shift.id, {
      totalSales: shift.totalSales + amount,
      salesCount: shift.salesCount + 1,
      ...this.methodDelta(shift, method, amount)
    })
  }

  /**
   * Учесть hold-чек (65.14): деньги уже в ящике, но фискальной выручки нет.
   * Счётчик `salesCount` не растёт — до решения это не продажа.
   */
  async recordHold(amount: number, method = 'cash'): Promise<void> {
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) return
    const abs = Math.abs(amount)
    await this.db.shifts.update(shift.id, {
      heldTotal: (shift.heldTotal ?? 0) + abs,
      heldCount: (shift.heldCount ?? 0) + 1,
      ...this.methodDelta(shift, method, abs)
    })
  }

  /**
   * 65.14/9c: атомарно учесть результат hold-чека в смене и поставить
   * маркер на чеке. Повторный вызов после recovery — no-op.
   *
   * Проверяем фактический статус чека: approve применяется только к
   * fiscalized, cancel — только к cancelled. Это защищает от гонки с UI.
   */
  async applyHoldResolution(
    receiptId: string,
    resolution: 'approve' | 'cancel'
  ): Promise<boolean> {
    const expectedStatus = resolution === 'approve' ? 'fiscalized' : 'cancelled'
    // Idempotent no-op проверяем до поиска активной смены: после успешного
    // применения смена могла уже закрыться, но повтор должен быть успешным.
    const existing = await this.db.receipts
      .where('receiptId')
      .equals(receiptId)
      .first()
    if (!existing || existing.id == null) return false
    if (existing.status !== expectedStatus) return false
    if (existing.holdShiftAppliedAt) return true

    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) return false
    const shiftId = shift.id
    let applied = false
    await this.db.transaction(
      'rw',
      [this.db.receipts, this.db.shifts],
      async () => {
        const receipt = await this.db.receipts
          .where('receiptId')
          .equals(receiptId)
          .first()
        if (!receipt || receipt.id == null) return
        if (receipt.status !== expectedStatus) return
        // Уже учтено — это успешный идемпотентный повтор, а не ошибка.
        if (receipt.holdShiftAppliedAt) {
          applied = true
          return
        }
        const current = await this.db.shifts.get(shiftId)
        if (!current) return
        // Recovery может произойти после закрытия исходной смены и открытия
        // новой. Нельзя записывать старый hold в текущую смену: сверяем зону и
        // номер, как это делает SaleService.assertActiveShiftFor.
        if (receipt.shiftNumber != null && current.shiftNumber !== receipt.shiftNumber) {
          return
        }
        if (receipt.zoneId && current.zoneId && current.zoneId !== receipt.zoneId) {
          return
        }
        const abs = Math.abs(receipt.total)
        const method = receipt.paymentMethod ?? 'cash'
        if (resolution === 'approve') {
          await this.db.shifts.update(shiftId, {
            totalSales: current.totalSales + abs,
            salesCount: current.salesCount + 1,
            heldTotal: Math.max(0, (current.heldTotal ?? 0) - abs),
            heldCount: Math.max(0, (current.heldCount ?? 0) - 1)
          })
        } else {
          await this.db.shifts.update(shiftId, {
            heldTotal: Math.max(0, (current.heldTotal ?? 0) - abs),
            heldCount: Math.max(0, (current.heldCount ?? 0) - 1),
            ...this.methodDelta(current, method, -abs)
          })
        }
        await this.db.receipts.update(receipt.id, {
          holdShiftAppliedAt: Date.now()
        })
        applied = true
      }
    )
    return applied
  }

  /**
   * 65.14: hold-чек успешно пробит. Деньги уже в ящике (recordHold добавил
   * их в метод), поэтому добавляем только продажу и снимаем held-счётчики.
   * С receiptId операция идемпотентна (9c recovery).
   */
  async releaseHold(amount: number, _method = 'cash', receiptId?: string): Promise<boolean> {
    if (receiptId) {
      return this.applyHoldResolution(receiptId, 'approve')
    }
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) return false
    const abs = Math.abs(amount)
    await this.db.shifts.update(shift.id, {
      totalSales: shift.totalSales + abs,
      salesCount: shift.salesCount + 1,
      heldTotal: Math.max(0, (shift.heldTotal ?? 0) - abs),
      heldCount: Math.max(0, (shift.heldCount ?? 0) - 1)
    })
    return true
  }

  /**
   * 65.14: hold-чек отменён с возвратом наличных. Убираем деньги из ящика
   * и held-счётчики; продажей он не становится.
   */
  async cancelHold(amount: number, method = 'cash', receiptId?: string): Promise<boolean> {
    if (receiptId) {
      return this.applyHoldResolution(receiptId, 'cancel')
    }
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) return false
    const abs = Math.abs(amount)
    await this.db.shifts.update(shift.id, {
      heldTotal: Math.max(0, (shift.heldTotal ?? 0) - abs),
      heldCount: Math.max(0, (shift.heldCount ?? 0) - 1),
      ...this.methodDelta(shift, method, -abs)
    })
    return true
  }

  /**
   * Учесть возврат (ARCH-TASK-65): суммы уменьшаются, счётчик ЧЕКОВ продаж
   * не растёт — возвраты считаются отдельно.
   */
  async recordReturn(amount: number, method = 'cash'): Promise<void> {
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) return

    const abs = Math.abs(amount)
    await this.db.shifts.update(shift.id, {
      totalSales: shift.totalSales - abs,
      returnsCount: (shift.returnsCount ?? 0) + 1,
      returnsTotal: (shift.returnsTotal ?? 0) + abs,
      ...this.methodDelta(shift, method, -abs)
    })
  }

  /** Дельта по способу оплаты: продажа +, возврат −. */
  private methodDelta(shift: PosShift, method: string, delta: number): Partial<PosShift> {
    if (method === 'cash') return { totalCash: (shift.totalCash ?? 0) + delta }
    if (method === 'card') return { totalCard: (shift.totalCard ?? 0) + delta }
    if (method === 'sbp') return { totalSbp: (shift.totalSbp ?? 0) + delta }
    return {}
  }

  /**
   * Закрыть смену и сформировать Z-отчёт.
   *
   * На кассе смену закрывает ТОЛЬКО устройство-открыватель (openedByDevice) —
   * иначе устройство-сосед по зоне получит понятную ошибку (закрытие доступно
   * с кассы-открывателя или из админки директора). Перед фиксацией Z-отчёта
   * досылаем накопленные чеки (forward-look), чтобы выручка по зоне была полной.
   */
  async closeShift(options: CloseShiftOptions = {}): Promise<ShiftSummary> {
    const shift = await this.getActiveShift()
    if (!shift || shift.id == null) {
      throw new Error('Нет активной открытой смены для закрытия')
    }

    const currentDeviceId = await this.localDeviceId()
    if (shift.openedByDevice && shift.openedByDevice !== currentDeviceId) {
      throw new Error(
        'Смену закрывают на кассе-открывателе (или из админки директора)'
      )
    }

    // 65.14: смена не закрывается, пока есть hold-чеки и незавершённые
    // замены non_fiscal → fiscal.
    await assertNoHeldReceipts(this.db)
    await assertNoUnaccountedHolds(this.db)
    await assertNoPendingReplacements(this.db)

    // Очередь дофискализации: локальные offline-чеки и общая очередь зоны
    // должны быть разобраны до Z. Иначе чек останется вне фискального отчёта.
    const localPending = await this.db.offlineQueue
      .where('type')
      .equals('fiscalize')
      .filter((item) => item.status === 'pending')
      .count()
    if (localPending > 0) {
      throw new Error(
        `Есть ${localPending} чек(ов) в очереди дофискализации. ` +
          'Дождитесь печати на ККТ и закройте смену после неё.'
      )
    }
    const online = typeof navigator === 'undefined' || navigator.onLine
    const queueToken = online ? await this.authToken() : null
    if (online && shift.zoneId && queueToken) {
      try {
        const queued = await syncService.getFiscalizationPendingCount(shift.zoneId)
        if (queued > 0) {
          throw new Error(
            `В зоне ${queued} чек(ов) ждут дофискализации на POS. ` +
              'Закрытие смены заблокировано.'
          )
        }
      } catch (error) {
        // Не разрешаем закрыть Z, если облачная очередь недоступна: иначе
        // телефоны останутся с офлайн-чеками вне фискального отчёта.
        if (error instanceof Error && error.message.includes('ждут дофискализации')) {
          throw error
        }
        throw new Error(
          `Не удалось проверить очередь дофискализации: ${
            error instanceof Error ? error.message : String(error)
          }`
        )
      }
    }

    const kktZIncomplete =
      (shift.kktExternalReceipts ?? 0) > 0 ||
      shift.kktZComplete === false ||
      (options.requireKktZ === true &&
        options.kktDocumentsReadable !== false &&
        shift.kktZComplete !== true)
    if (kktZIncomplete) {
      throw new Error(
        `Смена ККТ не сверена полностью: внешних документов ${shift.kktExternalReceipts ?? 0}, ` +
          `состояние сверки ${shift.kktZComplete === true ? 'полное' : 'не подтверждено'}. ` +
          'Закройте смену на ККТ или в ПО, открывшем смену.'
      )
    }

    // Форвард-лук: чеки уходят в облако до формирования Z-отчёта.
    await this.flushReceipts()

    // Сначала закрываем ККТ. Если Z не сформирован, локальная смена остаётся
    // открытой: иначе POS покажет закрытую смену при открытой ККТ.
    if (options.kktClose) {
      const result = await options.kktClose()
      if (!result.success) {
        throw new Error(
          `ККТ не закрыла смену: ${result.error ?? 'неизвестная ошибка'}. ` +
            'Смена POS оставлена открытой.'
        )
      }
    }

    const closedAt = Date.now()
    const partial = {
      salesCount: shift.salesCount,
      totalSales: shift.totalSales,
      cashTotal: shift.totalCash ?? 0,
      cardTotal: shift.totalCard ?? 0,
      sbpTotal: shift.totalSbp ?? 0,
      returnsTotal: shift.returnsTotal ?? 0,
      returnsCount: shift.returnsCount ?? 0
    }

    // Полный Z по ЗОНЕ (все устройства рабочего места) берём из облака — касса
    // офлайн видит только свой вклад, поэтому агрегирует сервер. Онлайн-закрытие
    // даёт полный отчёт, офлайн — частичный с пометкой incomplete.
    let full: ShiftZReport | null = null
    if (online && shift.zoneId) {
      full = await syncService.fetchShiftZ(shift.zoneId, shift.shiftNumber)
    }

    const summary: ShiftSummary = {
      shift: { ...shift, closedAt, status: 'closed' },
      salesCount: partial.salesCount,
      totalRevenue: partial.totalSales,
      returnsTotal: partial.returnsTotal,
      returnsCount: partial.returnsCount,
      cashTotal: partial.cashTotal,
      cardTotal: partial.cardTotal,
      sbpTotal: partial.sbpTotal,
      partial,
      full: full ?? undefined,
      incomplete: full == null
    }

    await this.db.shifts.update(shift.id, {
      status: 'closed',
      closedAt,
      zReportData: JSON.stringify(summary)
    })
    await this.db.meta.delete(META_ACTIVE_SHIFT_ID)

    // Отправляем закрытую смену с Z-отчётом в облако.
    this.pushShiftsInBackground()

    return summary
  }
}
